import { useRef, useState } from "react";
import { Camera, Check, Eye, EyeOff, UserCircle2 } from "lucide-react";
import { API, api, mediaUrl } from "@/lib/api";

const FIELDS = [["name", "Full name"], ["designation", "Designation / role"], ["organization", "Organization"], ["city", "City"], ["phone", "Phone"], ["qualification", "Qualification"], ["institution", "Diploma institution"], ["passing_year", "Year of passing"], ["linkedin", "LinkedIn URL (https://…)"]];
const VIS = [["picture", "Profile photo"], ["designation", "Designation"], ["organization", "Organization"], ["city", "City"], ["qualification", "Qualification"], ["bio", "About me"], ["linkedin", "LinkedIn"], ["email", "Email address"], ["phone", "Phone number"]];
const DEFAULT_VIS = { email: false, phone: false, organization: true, city: true, qualification: true, designation: true, bio: true, linkedin: true, picture: true };

export const ProfileEditor = ({ user, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries([...FIELDS.map(([k]) => [k, user[k] || ""]), ["bio", user.bio || ""]]));
  const [vis, setVis] = useState({ ...DEFAULT_VIS, ...(user.visibility || {}) });
  const [picture, setPicture] = useState(user.picture || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef();

  const save = async e => {
    e.preventDefault(); setBusy(true); setErr(""); setMsg("");
    try { await api("/account/profile", { method: "PUT", body: JSON.stringify({ ...form, visibility: vis }) }); setMsg("Profile updated."); onSaved(); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };
  const upload = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/account/avatar`, { method: "POST", body: fd, credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Upload failed");
      setPicture(d.picture); onSaved();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <form className="account-card profile-card" onSubmit={save} data-testid="profile-editor">
      <h3><UserCircle2 size={18} /> My profile</h3>
      <div className="avatar-row">
        {picture ? <img src={mediaUrl(picture)} alt="" data-testid="profile-avatar" /> : <span className="avatar-blank">{(user.name || "M")[0]}</span>}
        <button type="button" className="button button-ghost dark" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-avatar-upload"><Camera size={14} /> Change photo</button>
        <input ref={fileRef} type="file" hidden accept="image/*" onChange={upload} data-testid="profile-avatar-file" />
      </div>
      <div className="profile-fields">
        {FIELDS.map(([k, l]) => (
          <label key={k}>{l}<input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`profile-${k}-input`} /></label>
        ))}
        <label className="full">About me<textarea rows={3} maxLength={600} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio-input" /></label>
      </div>
      <div>
        <span className="card-kicker">Visible in public directory</span>
        <div className="vis-grid" data-testid="visibility-toggles">
          {VIS.map(([k, l]) => (
            <button type="button" key={k} className={vis[k] ? "vis-chip on" : "vis-chip"} onClick={() => setVis({ ...vis, [k]: !vis[k] })} aria-pressed={vis[k]} data-testid={`visibility-${k}`}>
              {vis[k] ? <Eye size={13} /> : <EyeOff size={13} />} {l}
            </button>
          ))}
        </div>
      </div>
      {msg && <div className="success-message" data-testid="profile-success">{msg}</div>}
      {err && <div className="error-message" data-testid="profile-error">{err}</div>}
      <button className="button button-gold" disabled={busy} data-testid="profile-save-button"><Check size={15} /> {busy ? "Saving…" : "Save profile"}</button>
    </form>
  );
};
